import { DiagnosticQuestion } from '../types';
import { Language } from '../i18n/translations';

export const getDiagnosticQuestions = (language: Language): DiagnosticQuestion[] => {
  const questions = {
    en: [
      {
        id: 'time',
        question: 'How much time does this task consume weekly?',
        icon: '⏱️',
        type: 'time' as const,
        reason: 'Time is money. We quantify hours spent to calculate your ROI and prioritize high-impact automations.'
      },
      {
        id: 'money',
        question: 'What is the direct cost of this inefficiency?',
        icon: '💰',
        type: 'money' as const,
        reason: 'Understanding hard costs helps us demonstrate immediate financial impact and justify the investment.'
      },
      {
        id: 'focus',
        question: 'How much mental energy does it drain from strategic work?',
        icon: '🧠',
        type: 'focus' as const,
        reason: 'Hidden costs matter. Freeing mental bandwidth for strategic work often delivers greater value than time saved.'
      },
      {
        id: 'errors',
        question: 'How often do errors or delays occur?',
        icon: '⚠️',
        type: 'errors' as const,
        reason: 'Error rates reveal automation urgency. High-error processes benefit most from consistent automated execution.'
      },
      {
        id: 'trigger',
        question: 'What triggers this task to start?',
        icon: '🎯',
        type: 'trigger' as const,
        reason: 'Triggers define automation entry points. Clear triggers enable seamless integration into your workflow.'
      },
      {
        id: 'scalability',
        question: 'What happens when your business volume doubles?',
        icon: '📈',
        type: 'scalability' as const,
        reason: 'Growth planning is critical. We design solutions that scale with your business, not bottleneck it.'
      },
      {
        id: 'handoffs',
        question: 'How many people touch this process?',
        icon: '🤝',
        type: 'handoffs' as const,
        reason: 'Multiple handoffs create delays and errors. Automation reduces coordination overhead dramatically.'
      },
      {
        id: 'data',
        question: 'Where does the data come from and where does it go?',
        icon: '🔄',
        type: 'data' as const,
        reason: 'Data flow mapping ensures seamless integration. We connect your systems without disrupting existing workflows.'
      },
      {
        id: 'outcome',
        question: 'What does success look like for this workflow?',
        icon: '✨',
        type: 'outcome' as const,
        reason: 'Clear success metrics guide our design. We build solutions that deliver measurable, specific outcomes.'
      }
    ],
    es: [
      {
        id: 'time',
        question: '¿Cuánto tiempo consume esta tarea semanalmente?',
        icon: '⏱️',
        type: 'time' as const,
        reason: 'El tiempo es dinero. Cuantificamos las horas invertidas para calcular tu ROI y priorizar automatizaciones de alto impacto.'
      },
      {
        id: 'money',
        question: '¿Cuál es el costo directo de esta ineficiencia?',
        icon: '💰',
        type: 'money' as const,
        reason: 'Entender los costos reales nos ayuda a demostrar el impacto financiero inmediato y justificar la inversión.'
      },
      {
        id: 'focus',
        question: '¿Cuánta energía mental drena del trabajo estratégico?',
        icon: '🧠',
        type: 'focus' as const,
        reason: 'Los costos ocultos importan. Liberar capacidad mental para trabajo estratégico suele entregar mayor valor que el tiempo ahorrado.'
      },
      {
        id: 'errors',
        question: '¿Con qué frecuencia ocurren errores o retrasos?',
        icon: '⚠️',
        type: 'errors' as const,
        reason: 'Las tasas de error revelan urgencia de automatización. Los procesos con muchos errores se benefician más de una ejecución automatizada consistente.'
      },
      {
        id: 'trigger',
        question: '¿Qué desencadena el inicio de esta tarea?',
        icon: '🎯',
        type: 'trigger' as const,
        reason: 'Los disparadores definen puntos de entrada de automatización. Disparadores claros permiten integración perfecta en tu flujo de trabajo.'
      },
      {
        id: 'scalability',
        question: '¿Qué sucede cuando el volumen de tu negocio se duplica?',
        icon: '📈',
        type: 'scalability' as const,
        reason: 'La planificación de crecimiento es crítica. Diseñamos soluciones que escalan con tu negocio, no que lo limitan.'
      },
      {
        id: 'handoffs',
        question: '¿Cuántas personas intervienen en este proceso?',
        icon: '🤝',
        type: 'handoffs' as const,
        reason: 'Múltiples transferencias crean retrasos y errores. La automatización reduce dramáticamente la sobrecarga de coordinación.'
      },
      {
        id: 'data',
        question: '¿De dónde vienen los datos y hacia dónde van?',
        icon: '🔄',
        type: 'data' as const,
        reason: 'Mapear el flujo de datos asegura integración perfecta. Conectamos tus sistemas sin interrumpir flujos de trabajo existentes.'
      },
      {
        id: 'outcome',
        question: '¿Cómo se ve el éxito para este flujo de trabajo?',
        icon: '✨',
        type: 'outcome' as const,
        reason: 'Métricas de éxito claras guían nuestro diseño. Construimos soluciones que entregan resultados medibles y específicos.'
      }
    ]
  };

  return questions[language];
};
